/**
 * Reconciliation Report — admin/audit only.
 * Shows shortages & excesses at 3 grouping levels: Per Piece / Per Lot / Per Branch.
 */
import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import GlobalNavigationMenu from '../components/GlobalNavigationMenu';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { errMsg } from './oldgold/errors';
import { exportReconciliationCsv } from './oldgold/reconcileCsv';
import { RefreshCw, ScrollText, Package, Coins, Building2, Download, Printer } from 'lucide-react';

const CAT_LABEL = {
  diamond: 'Diamond', stone: 'Stone', beads_pearl: 'Beads/Pearl',
  dust: 'Dust', others: 'Others',
};
const CAT_KEYS = ['diamond', 'stone', 'beads_pearl', 'dust', 'others'];
const WEIGHT_ONLY = new Set(['dust', 'others']);
const VERDICT_COLOUR = {
  match: 'bg-emerald-100 text-emerald-800',
  short: 'bg-rose-100 text-rose-800',
  extra: 'bg-orange-100 text-orange-800',
  mixed: 'bg-amber-100 text-amber-800',
};

const DiffCell = ({ diff }) => {
  if (diff === 0 || diff === undefined) return <span className="text-slate-400">0</span>;
  return diff < 0
    ? <span className="text-rose-700 font-medium">↓ {Math.abs(diff)}</span>
    : <span className="text-orange-700 font-medium">↑ {diff}</span>;
};

const ReconciliationReport = () => {
  const [tab, setTab] = useState('pieces');
  const [filters, setFilters] = useState({
    branch: '', mia_id: '', lot_id: '', start: '', end: '', only: '',
  });
  const [miaUsers, setMiaUsers] = useState([]);

  const [piecesData, setPiecesData] = useState({ rows: [], totals: null });
  const [lotsData, setLotsData] = useState({ rows: [], totals: null });
  const [branchesData, setBranchesData] = useState({ rows: [], totals: null });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      for (const k of Object.keys(filters)) if (filters[k]) params[k] = filters[k];
      const endpoint = tab === 'pieces' ? 'pieces' : tab === 'lots' ? 'lots' : 'branches';
      const r = await api.get(`/old-gold/reconcile/${endpoint}`, { params });
      if (endpoint === 'pieces') setPiecesData(r.data);
      else if (endpoint === 'lots') setLotsData(r.data);
      else setBranchesData(r.data);
    } catch (err) {
      toast.error(errMsg(err, 'Failed to load report'));
    } finally {
      setLoading(false);
    }
  }, [tab, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get('/old-gold/mia-users').then(r => setMiaUsers(r.data)).catch(() => {});
  }, []);

  const clearFilters = () => setFilters({ branch: '', mia_id: '', lot_id: '', start: '', end: '', only: '' });

  const currentRows =
    tab === 'pieces' ? piecesData.rows
    : tab === 'lots' ? lotsData.rows
    : branchesData.rows;

  const onExportCsv = () => {
    if (!currentRows || currentRows.length === 0) {
      toast.error('Nothing to export — no rows in view');
      return;
    }
    exportReconciliationCsv(currentRows, tab);
    toast.success(`Exported ${currentRows.length} ${tab} row${currentRows.length === 1 ? '' : 's'}`);
  };

  const onPrint = () => window.print();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-amber-50/40 pb-16">
      <header className="bg-gradient-to-r from-amber-800 to-yellow-700 text-white shadow sticky top-0 z-30 print:hidden">
        <div className="max-w-7xl mx-auto px-3 py-2.5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ScrollText className="w-5 h-5 sm:w-6 sm:h-6" />
            <h1 className="text-base sm:text-xl font-semibold" data-testid="recon-title">
              Old Gold — Reconciliation Report
            </h1>
          </div>
          <GlobalNavigationMenu />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 py-4 space-y-4">
        {/* Print-only header — shown only when the page is being printed. */}
        <div className="hidden print:block mb-2">
          <h1 className="text-xl font-semibold">Old Gold — Reconciliation Report</h1>
          <div className="text-xs text-slate-600">
            View: {tab === 'pieces' ? 'Per Piece' : tab === 'lots' ? 'Per Lot' : 'Per Branch'} ·
            Printed: {new Date().toLocaleString()}
          </div>
        </div>
        {/* Filters */}
        <div className="bg-white rounded-xl shadow border border-amber-100 p-3 space-y-2 print:hidden">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
            <div>
              <Label className="text-xs">Branch</Label>
              <Input value={filters.branch} onChange={(e) => setFilters({ ...filters, branch: e.target.value })}
                placeholder="Any" className="h-10" data-testid="recon-filter-branch" />
            </div>
            <div>
              <Label className="text-xs">MIA</Label>
              <Select value={filters.mia_id || 'all'} onValueChange={(v) => setFilters({ ...filters, mia_id: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-10" data-testid="recon-filter-mia"><SelectValue placeholder="Any" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any</SelectItem>
                  {miaUsers.map(u => (
                    <SelectItem key={u.id} value={u.id}>{u.username}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Lot no.</Label>
              <Input value={filters.lot_id} onChange={(e) => setFilters({ ...filters, lot_id: e.target.value })}
                placeholder="e.g. OGLOT-2026-0001" className="h-10" data-testid="recon-filter-lot" />
            </div>
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })}
                className="h-10" data-testid="recon-filter-start" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })}
                className="h-10" data-testid="recon-filter-end" />
            </div>
            <div>
              <Label className="text-xs">Show</Label>
              <Select value={filters.only || 'all'} onValueChange={(v) => setFilters({ ...filters, only: v === 'all' ? '' : v })}>
                <SelectTrigger className="h-10" data-testid="recon-filter-only"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="mismatch">Mismatch only</SelectItem>
                  <SelectItem value="short">Short only</SelectItem>
                  <SelectItem value="extra">Extra only</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={clearFilters} data-testid="recon-clear-btn">Clear</Button>
            <Button size="sm" onClick={load} className="bg-amber-700 hover:bg-amber-800" data-testid="recon-refresh-btn">
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={onExportCsv}
              disabled={loading || !currentRows || currentRows.length === 0}
              data-testid="recon-export-csv-btn"
              className="border-amber-300 text-amber-800 hover:bg-amber-50 print:hidden"
            >
              <Download className="w-4 h-4 mr-1" /> Export CSV
            </Button>
            <Button
              size="sm" variant="outline"
              onClick={onPrint}
              disabled={loading || !currentRows || currentRows.length === 0}
              data-testid="recon-print-btn"
              className="border-slate-300 print:hidden"
            >
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="w-full grid grid-cols-3 print:hidden">
            <TabsTrigger value="pieces" data-testid="recon-tab-pieces"><Package className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Per Piece</span></TabsTrigger>
            <TabsTrigger value="lots" data-testid="recon-tab-lots"><Coins className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Per Lot</span></TabsTrigger>
            <TabsTrigger value="branches" data-testid="recon-tab-branches"><Building2 className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Per Branch</span></TabsTrigger>
          </TabsList>

          {/* ===== Per Piece ===== */}
          <TabsContent value="pieces" className="mt-3">
            {piecesData.totals && (
              <TotalsStrip totals={[
                ['Pieces', piecesData.totals.pieces],
                ['Match', piecesData.totals.match_pieces, 'emerald'],
                ['Mismatch', piecesData.totals.mismatch_pieces, 'rose'],
                ['Total short (pcs)', piecesData.totals.short_pcs, 'rose'],
                ['Total extra (pcs)', piecesData.totals.extra_pcs, 'orange'],
              ]} />
            )}
            <div className="bg-white rounded-xl shadow border border-amber-100 mt-3 divide-y" data-testid="recon-pieces-list">
              {loading && <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>}
              {!loading && piecesData.rows.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No rows</div>}
              {piecesData.rows.map((r) => (
                <div key={r.piece_id} className="p-3" data-testid={`recon-piece-${r.serial_no}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-mono text-xs">{r.serial_no}</span>
                    <Badge className={VERDICT_COLOUR[r.status] || ''}>{r.status}</Badge>
                    <span className="text-xs text-slate-500 truncate">
                      {r.branch} · {r.lot_no}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {CAT_KEYS.map((c) => (
                      <CatRow key={c} label={CAT_LABEL[c]} data={r.categories[c]} weightOnly={WEIGHT_ONLY.has(c)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ===== Per Lot ===== */}
          <TabsContent value="lots" className="mt-3">
            {lotsData.totals && (
              <TotalsStrip totals={[
                ['Lots', lotsData.totals.lots],
                ['Total short (pcs)', lotsData.totals.short_pcs, 'rose'],
                ['Total extra (pcs)', lotsData.totals.extra_pcs, 'orange'],
              ]} />
            )}
            <div className="bg-white rounded-xl shadow border border-amber-100 mt-3 divide-y" data-testid="recon-lots-list">
              {loading && <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>}
              {!loading && lotsData.rows.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No rows</div>}
              {lotsData.rows.map((r) => (
                <div key={r.lot_id} className="p-3" data-testid={`recon-lot-${r.lot_no}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-mono text-xs">{r.lot_no}</span>
                    <Badge className={VERDICT_COLOUR[r.verdict] || ''}>{r.verdict}</Badge>
                    <span className="text-xs text-slate-500 truncate">
                      {r.assigned_to_name} · {r.pieces_broken} pcs
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {CAT_KEYS.map((c) => (
                      <CatRow key={c} label={CAT_LABEL[c]} data={r.categories[c]} weightOnly={WEIGHT_ONLY.has(c)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* ===== Per Branch ===== */}
          <TabsContent value="branches" className="mt-3">
            {branchesData.totals && (
              <TotalsStrip totals={[
                ['Branches', branchesData.totals.branches],
                ['Total short (pcs)', branchesData.totals.short_pcs, 'rose'],
                ['Total extra (pcs)', branchesData.totals.extra_pcs, 'orange'],
              ]} />
            )}
            <div className="bg-white rounded-xl shadow border border-amber-100 mt-3 divide-y" data-testid="recon-branches-list">
              {loading && <div className="p-6 text-center text-slate-400 text-sm">Loading…</div>}
              {!loading && branchesData.rows.length === 0 && <div className="p-6 text-center text-slate-400 text-sm">No rows</div>}
              {branchesData.rows.map((r) => (
                <div key={r.branch} className="p-3" data-testid={`recon-branch-${r.branch}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-medium">{r.branch}</span>
                    <Badge className={VERDICT_COLOUR[r.verdict] || ''}>{r.verdict}</Badge>
                    <span className="text-xs text-slate-500">{r.pieces_broken} pcs broken</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    {CAT_KEYS.map((c) => (
                      <CatRow key={c} label={CAT_LABEL[c]} data={r.categories[c]} weightOnly={WEIGHT_ONLY.has(c)} />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const TotalsStrip = ({ totals }) => (
  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
    {totals.map(([label, value, tone]) => (
      <div
        key={label}
        className={`rounded-lg border py-2 px-3 ${
          tone === 'rose' ? 'bg-rose-50 border-rose-200 text-rose-800'
          : tone === 'orange' ? 'bg-orange-50 border-orange-200 text-orange-800'
          : tone === 'emerald' ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
          : 'bg-slate-50 border-slate-200 text-slate-800'
        }`}
      >
        <div className="text-[10px] uppercase tracking-wide opacity-80">{label}</div>
        <div className="text-lg font-semibold">{value}</div>
      </div>
    ))}
  </div>
);

const CatRow = ({ label, data, weightOnly }) => {
  const expWt = data?.expected_wt || 0;
  const actWt = data?.actual_wt || 0;
  const diffWt = data?.diff_wt || 0;
  if (weightOnly) {
    // No pcs — show only weight expected vs actual
    if (expWt === 0 && actWt === 0) return null;
    return (
      <div className="rounded border bg-slate-50 border-slate-200 px-2 py-1.5">
        <div className="text-[10px] uppercase text-slate-500">{label}</div>
        <div className="text-[11px] text-slate-600">
          {expWt.toFixed(3)} → <b>{actWt.toFixed(3)}</b> gm
        </div>
        {Math.abs(diffWt) > 0.0005 && (
          <div className={`text-[10px] font-medium ${diffWt < 0 ? 'text-rose-700' : 'text-orange-700'}`}>
            {diffWt < 0 ? '↓' : '↑'} {Math.abs(diffWt).toFixed(3)} gm
          </div>
        )}
      </div>
    );
  }
  return (
    <div className="rounded border bg-slate-50 border-slate-200 px-2 py-1.5">
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className="text-xs">
        <span className="text-slate-700">{data?.expected || 0}</span>
        <span className="text-slate-400"> → </span>
        <span className="font-medium">{data?.actual || 0}</span>
        <span className="text-[10px] text-slate-400 ml-1">pcs</span>
      </div>
      <div className="text-[11px]"><DiffCell diff={data?.diff || 0} /></div>
      {(expWt > 0 || actWt > 0) && (
        <div className="mt-1 pt-1 border-t border-slate-200">
          <div className="text-[11px] text-slate-600">
            {expWt.toFixed(3)} → <b>{actWt.toFixed(3)}</b> gm
          </div>
          {Math.abs(diffWt) > 0.0005 && (
            <div className={`text-[10px] font-medium ${diffWt < 0 ? 'text-rose-700' : 'text-orange-700'}`}>
              {diffWt < 0 ? '↓' : '↑'} {Math.abs(diffWt).toFixed(3)} gm
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ReconciliationReport;
